import { useState } from 'react';
import { m as motion, AnimateSharedLayout } from 'framer-motion';
import { breakpoints, Heading, vars, Stack } from '@etg/wings';
import { css, resetButtonStyle, cx } from '@eti/styles';
import { useTranslation } from '@eti/providers';
import { useQuery } from '@apollo/client';
import { ONE_WAY, RETURN } from '../../constants/tripTypesConstants';
import Loading from '../loading/components/Loading';
import PriceList from './PriceList';
import type { Location } from './models';
import { GET_BEST_PRICES_PER_PERIOD } from './queries';

const linkWrapperStyles = css`
  margin: 0 auto;

  @media (min-width: ${breakpoints._768}) {
    max-width: 750px;
  }

  @media (min-width: ${breakpoints._970}) {
    max-width: 970px;
  }

  @media (min-width: 1170px) {
    max-width: 1170px;
  }
`;

const flightTypeStyles = css`
  align-items: stretch;
  display: flex;
  flex-flow: row;
  justify-content: center;
  list-style-type: none;
  padding-bottom: 16px;

  @media (min-width: ${breakpoints._560}) {
    justify-content: start;
  }
`;

const activeTripTypeStyles = css`
  color: ${vars.colors.links.default};
`;

const tripTypeButtonStyles = css`
  ${resetButtonStyle};
  align-items: center;
  color: ${vars.colors.inputs.border};
  cursor: pointer;
  display: flex;
  font-weight: 700;
  justify-content: center;
  padding-inline: 5px;
  position: relative;

  &:hover {
    color: ${vars.colors.links.default};
  }
`;

const underlineStyles = css`
  background-color: ${vars.colors.links.default};
  bottom: -14px;
  height: 2px;
  left: 0;
  position: absolute;
  right: 0;
`;

const loadingStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 301px;

  @media (min-width: ${breakpoints._768}) {
    min-height: 239px;
  }
`;

export interface PriceListWrapperProps {
  origin: Location;
  destination: Location;
}

const PriceListWrapper = ({ origin, destination }: PriceListWrapperProps) => {
  const [selectedTab, setSelectedTab] = useState(RETURN);
  const PERIODS = [7, 30, 60];
  const LENGTH_OF_STAY_ONE_WAY = 0;
  const LENGTH_OF_STAY_ROUND_TRIP = 7;

  const bestPricesPerPeriodVariables = {
    destination: destination.iataCode,
    lengthOfStay: selectedTab === ONE_WAY ? LENGTH_OF_STAY_ONE_WAY : LENGTH_OF_STAY_ROUND_TRIP,
    origin: origin.iataCode,
    periods: PERIODS,
  };

  const { data, loading } = useQuery(GET_BEST_PRICES_PER_PERIOD, {
    fetchPolicy: 'no-cache',
    variables: bestPricesPerPeriodVariables,
  });

  const { t } = useTranslation();

  const handleTabClick = (selectedType: string) => {
    setSelectedTab(selectedType);
  };

  return (
    <div data-testid="price-list-wrapper">
      <div className={linkWrapperStyles}>
        <Stack spacing={16}>
          <Heading data-testid="price-list-header" level={2}>
            {t('SemComponent.PriceLists.Header', origin.name, destination.name)}
          </Heading>

          <Stack spacing={{ [breakpoints._0]: 8, [breakpoints._560]: 16 }}>
            <div className={flightTypeStyles}>
              <AnimateSharedLayout>
                <button
                  className={cx(
                    tripTypeButtonStyles,
                    selectedTab === RETURN && activeTripTypeStyles,
                  )}
                  data-testid="price-list-return-button"
                  onClick={() => handleTabClick(RETURN)}
                  type="button"
                >
                  {t('SemComponent.PriceLists.Return')}
                  {selectedTab === RETURN && (
                    <motion.div className={underlineStyles} initial={false} layoutId="underline" />
                  )}
                </button>
                <button
                  className={cx(
                    tripTypeButtonStyles,
                    selectedTab === ONE_WAY && activeTripTypeStyles,
                  )}
                  data-testid="price-list-oneWay-button"
                  onClick={() => handleTabClick(ONE_WAY)}
                  type="button"
                >
                  {t('SemComponent.PriceLists.OneWay')}
                  {selectedTab === ONE_WAY && (
                    <motion.div className={underlineStyles} initial={false} layoutId="underline" />
                  )}
                </button>
              </AnimateSharedLayout>
            </div>

            <div>
              {!loading && data?.bestPricesPerPeriod ? (
                <motion.div
                  animate={{ opacity: 1 }}
                  initial={{ opacity: 0 }}
                  transition={{
                    delay: 0.1,
                    duration: 0.3,
                  }}
                >
                  <PriceList
                    destination={destination}
                    flights={data.bestPricesPerPeriod.bestPricesPerPeriod}
                    origin={origin}
                    tripType={selectedTab}
                  />
                </motion.div>
              ) : (
                <div className={loadingStyles}>
                  <Loading />
                </div>
              )}
            </div>
          </Stack>
        </Stack>
      </div>
    </div>
  );
};

export default PriceListWrapper;
